/**
 * useDashboardLayout — per-user dashboard arrangement over a widget catalog.
 *
 * The page supplies a `catalog` (all available widgets: {id, type, colSpan,
 * title, props}) and a `defaultLayout` ([{id, colSpan?, hidden?}]). This hook
 * loads the user's saved arrangement (GET /v2/layout), reconciles it with the
 * current catalog (drops removed widgets, appends newly-added ones), and
 * exposes edit actions + save/reset.
 *
 * The layout persisted is arrangement-only (id/colSpan/hidden) — widget
 * type/props always come from the catalog, so users never inject data
 * selections (matches the backend sanitizer).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getLayout, saveLayout, resetLayout } from '../api/client';

function reconcile(saved, catalog, defaultLayout) {
  const catalogIds = catalog.map((w) => w.id);
  const base = Array.isArray(saved) && saved.length ? saved : defaultLayout;
  // Keep only widgets that still exist in the catalog…
  const known = base.filter((e) => catalogIds.includes(e.id));
  // …then append any catalog widgets not already in the layout as HIDDEN. This
  // keeps the default/saved arrangement lean (only its widgets render) while
  // every other catalog widget stays discoverable in the Customize "Hidden
  // widgets" picker. (Appending them visible would dump the whole catalog onto
  // the page — the bug that made the lean default show everything.)
  const present = new Set(known.map((e) => e.id));
  const appended = catalog.filter((w) => !present.has(w.id)).map((w) => ({ id: w.id, hidden: true }));
  return [...known, ...appended];
}

export function useDashboardLayout({ dashboardKey, catalog = [], defaultLayout = [] }) {
  const catalogMap = useMemo(
    () => Object.fromEntries(catalog.map((w) => [w.id, w])),
    [catalog],
  );

  const [layout, setLayout] = useState(null); // [{id, colSpan?, hidden?}]
  // True when what is on screen came from the user's own SAVED arrangement
  // rather than the page default. A shell uses it to offer "Reset layout"
  // without entering edit mode — otherwise a user sitting on an arrangement
  // saved long before the default changed has no visible way back to it.
  const [customised, setCustomised] = useState(false);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    getLayout(dashboardKey)
      .then((saved) => {
        if (!live) return;
        setCustomised(Array.isArray(saved) && saved.length > 0);
        setLayout(reconcile(saved, catalog, defaultLayout));
      })
      .catch(() => { if (live) { setCustomised(false); setLayout(reconcile(null, catalog, defaultLayout)); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [dashboardKey]); // reload only on dashboard change; catalog/default are stable per page

  // Resolved, ordered entries with their catalog definition attached.
  const entries = useMemo(
    () => (layout || [])
      .map((e) => ({ ...e, def: catalogMap[e.id] }))
      .filter((e) => e.def),
    [layout, catalogMap],
  );

  const mutate = useCallback((fn) => {
    setLayout((prev) => fn(prev || []));
    setDirty(true);
  }, []);

  const move = useCallback((id, dir) => mutate((prev) => {
    const i = prev.findIndex((e) => e.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= prev.length) return prev;
    const next = [...prev];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  }), [mutate]);

  const toggleHide = useCallback((id) => mutate((prev) =>
    prev.map((e) => (e.id === id ? { ...e, hidden: !e.hidden } : e))), [mutate]);

  const cycleSpan = useCallback((id) => mutate((prev) =>
    prev.map((e) => {
      if (e.id !== id) return e;
      const cur = e.colSpan ?? catalogMap[e.id]?.colSpan ?? 1;
      return { ...e, colSpan: cur === 2 ? 1 : 2 };
    })), [mutate, catalogMap]);

  const save = useCallback(async () => {
    const clean = (layout || []).map((e) => ({
      id: e.id,
      ...(e.colSpan ? { colSpan: e.colSpan } : {}),
      ...(e.hidden ? { hidden: true } : {}),
    }));
    await saveLayout(dashboardKey, clean);
    setCustomised(true);
    setDirty(false);
    setEditing(false);
  }, [dashboardKey, layout]);

  const reset = useCallback(async () => {
    await resetLayout(dashboardKey).catch(() => {});
    setLayout(reconcile(null, catalog, defaultLayout));
    setCustomised(false);
    setDirty(false);
    setEditing(false);
  }, [dashboardKey, catalog, defaultLayout]);

  return { entries, editing, setEditing, dirty, loading, customised, move, toggleHide, cycleSpan, save, reset };
}

export default useDashboardLayout;
